import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Company, PagedResponse } from 'mbs-ui-shared';
import Computer from 'mbs-ui-shared@models/Computer';
import { SAWithDestinations } from 'mbs-ui-shared@models/StorageAccountsWithDestinations';
import { RemoteDeployConfiguration } from 'mbs-ui-shared@models/remote-deploy/remote-deploy-configuration';
import { RDComputersWithStatus } from 'mbs-ui-shared@models/remote-deploy/remote-deploy-configurations-history-computer';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const REMOTE_DEPLOY_CONFIGURATIONS_API = 'api/remote-deploy/configurations';

@Injectable({
  providedIn: 'root'
})
export class RemoteDeployDataService {
  private http = inject(HttpClient);

  getAll(): Observable<PagedResponse<RemoteDeployConfiguration>> {
    return this.http.get<PagedResponse<RemoteDeployConfiguration>>(REMOTE_DEPLOY_CONFIGURATIONS_API);
  }

  getWithQuery(params: Record<string, any>): Observable<PagedResponse<RemoteDeployConfiguration>> {
    return this.http.get<PagedResponse<RemoteDeployConfiguration>>(REMOTE_DEPLOY_CONFIGURATIONS_API, { params });
  }

  getByKey(id: string): Observable<RemoteDeployConfiguration> {
    return this.http.get<RemoteDeployConfiguration>(`${REMOTE_DEPLOY_CONFIGURATIONS_API}/${id}`);
  }

  add(config: RemoteDeployConfiguration): Observable<RemoteDeployConfiguration> {
    return this.http.post<RemoteDeployConfiguration>(REMOTE_DEPLOY_CONFIGURATIONS_API, config);
  }

  update(config: RemoteDeployConfiguration): Observable<RemoteDeployConfiguration> {
    return this.http.put<RemoteDeployConfiguration>(`api/remote-deploy/configurations/${config.id}`, config);
  }

  delete(id: string): Observable<any> {
    return this.http.delete(`api/remote-deploy/configurations/${id}`);
  }

  getDestinations$(data: any): Observable<SAWithDestinations[]> {
    return this.http.post<SAWithDestinations[]>('api/remote-deploy/destinations', data, {
      headers: { 'Content-Type': 'application/json; charset=utf-8' }
    });
  }

  postConfigComputers$(configId: string, bodyParams: any): Observable<RDComputersWithStatus[]> {
    return this.http
      .post<RDComputersWithStatus[]>(`/api/remote-deploy/configurations/${configId}/computers`, bodyParams)
      .pipe(map((response) => ((response as any).data ? ((response as any).data as RDComputersWithStatus[]) : response)));
  }

  getTemplatesByTags(tags: number[]): Observable<{ data: RemoteDeployConfiguration[] }> {
    return this.http.get<{ data: RemoteDeployConfiguration[] }>('/api/remote-deploy/configurations', { params: { selectedTags: tags } });
  }

  getConfigSelectedComputers(configuration: RemoteDeployConfiguration): Observable<Computer[]> {
    return this.http.get<Computer[]>(`/api/remote-deploy/configurations/${configuration.id}/SelectedComputers`);
  }

  getConfigSelectedCompanies(configuration: RemoteDeployConfiguration): Observable<Company[]> {
    return this.http.get<Company[]>(`/api/remote-deploy/configurations/${configuration.id}/SelectedCompanies`);
  }

  cloneConfiguration(id: string): Observable<RemoteDeployConfiguration> {
    return this.http.get<RemoteDeployConfiguration>(`/api/remote-deploy/configurations/${id}/clone`, {
      headers: new HttpHeaders({ MANUAL_ERROR_HANDLED: 'MANUAL_ERROR_HANDLED' })
    });
  }

  importConfiguration(computerId: string): Observable<any> {
    return this.http.get(`/api/computers/${computerId}/importconfiguration`);
  }

  importEnabledConfiguration(computerId: string): Observable<any> {
    return this.http.get(`/api/computers/${computerId}/importconfiguration`, { params: { enabled: true } });
  }

  applyConfigurationToComputer(configId: string, computerId: string, enable: boolean): Observable<any> {
    return this.http.post(`/api/remote-deploy/configurations/${configId}/applytocomputer/${computerId}`, { enable });
  }
}
